import { useCallback, useEffect, useState } from 'react';

import { EVENTS } from '../../../../fitment-constants';
import { toFitmentSize } from '../../../../fitment-utils';

import {
  parseAssemblyType,
  parseTireSize
} from '../../../../../../shared/utils/fitment/fitment';

interface ByOffsetParams {
  offset: string;
}

interface vehicleSelectionParams {
  id: string;
}

interface RideHeightParams {
  description: string;
  id: string;
  type: string;
}

interface VehicleParams {
  assemblyId?: string;
  rideHeight?: RideHeightParams;
  trimId?: string;
  vehicleId?: string;
}

function byOffset(a: ByOffsetParams, b: ByOffsetParams) {
  return parseFloat(a.offset) - parseFloat(b.offset);
}

function getParam(
  assembly?: vehicleSelectionParams,
  model?: vehicleSelectionParams,
  trim?: vehicleSelectionParams,
  rideHeight?: RideHeightParams
): VehicleParams {
  const params: VehicleParams = {
    assemblyId: assembly?.id,
    trimId: trim?.id,
    vehicleId: model?.id
  };

  if (rideHeight) {
    params.rideHeight = {
      description: rideHeight?.description,
      id: rideHeight?.id,
      type: rideHeight?.type
    };
  }

  return params;
}

const AXLE_TYPES = {
  BOTH: 'B'
};

interface FitmentSelectType {
  description: string;
  id: string;
}

interface FitmentSelectTypeExtended extends FitmentSelectType {
  type: string;
}

interface GetOptionalSizesReturnType {
  optionalStaggeredSet: {
    matchQualifier: string;
    offset: string;
    size: { front: string; rear: string };
  }[];
  tireSizes: {
    axleType: string;
    optionalTireSizes: { matchQualifier: string; tireSize: string }[];
    sizeQualifier: string;
    wheelSize: string;
  }[];
}

type GetOptionalSizes = (
  params: VehicleParams
) => Promise<GetOptionalSizesReturnType>;

interface OnEventParams {
  name: string;
  payload?: unknown;
}

type OnEvent = (event: OnEventParams) => void;

type OnShopProducts = (
  selectedAssembly?: FitmentSelectType,
  selectedOptionalSize?: FitmentSelectType | null,
  sizeQualifier?: string
) => void;

interface SelectedFitment {
  assemblyId: string;
  frontTireSize: string;
  frontWheelDiameter: string;
  fuelType: string;
  isAWD: boolean;
  isActive: boolean;
  isDually: boolean;
  isOE: boolean;
  isStaggered: boolean;
  largeImageUrl: string;
  make: string;
  model: string;
  oeFrontTireSize: string;
  oeFrontWheelDiameter: string;
  oeRearTireSize: string;
  oeRearWheelDiameter: string;
  rearTireSize: string;
  rearWheelDiameter: string;
  rideHeight: string;
  rideHeightId: string;
  rideHeightType: string;
  sizeQualifier: string;
  smallImageUrl: string;
  trim: string;
  trimId: string;
  type: string;
  uid: string;
  vehicleAssemblyId: string;
  vehicleCategory: string;
  vehicleId: string;
  wheelTorque: number;
  year: string;
}

type SetSelectedAssembly = (assembly: FitmentSelectType) => void;
type SetIsViewingOptionalSizes = (isViewingOptionalSizes: boolean) => void;

interface Assembly extends FitmentSelectType {
  frontTireDescription: string;
  rearTireDescription: string;
}

interface FitmentSizeProps {
  assemblies?: FitmentSelectType[];
  getOptionalSizes: GetOptionalSizes;
  isEditing: boolean;
  isYearAfterModel: boolean;
  onEvent: OnEvent;
  onShopProducts: OnShopProducts;
  selectedAssembly?: FitmentSelectType;
  selectedFitment?: SelectedFitment;
  selectedModel?: FitmentSelectType;
  selectedRideHeight?: FitmentSelectTypeExtended;
  selectedTrim?: FitmentSelectType;
  selectedYear?: FitmentSelectType;
  setIsViewingOptionalSizes: SetIsViewingOptionalSizes;
  setSelectedAssembly: SetSelectedAssembly;
  shouldCallShopProducts: boolean;
}

interface Grouping {
  staggeredAssemblies: Assembly[];
  standardAssemblies: Assembly[];
}

interface TireSizeDetails {
  description: string;
  frontTireDescription: string;
  frontTireSize: string;
  rearTireDescription: string;
  rearTireSize: string;
}

interface OptionalSizes {
  offset: string;
  optionalTireSizes?: TireSizeDetails[];
  oversizedSizes: TireSizeDetails[];
  oversizedSizesStaggered: TireSizeDetails[];
  sizes: TireSizeDetails[];
  sizesStaggered: TireSizeDetails[];
  undersizedSizes: TireSizeDetails[];
  undersizedSizesStaggered: TireSizeDetails[];
  wheelSize: string;
}

export default function useFitmentSize(props: FitmentSizeProps) {
  const {
    assemblies,
    getOptionalSizes,
    isEditing,
    isYearAfterModel,
    onEvent,
    onShopProducts,
    selectedAssembly,
    selectedFitment,
    selectedModel,
    selectedRideHeight,
    selectedTrim,
    selectedYear,
    setIsViewingOptionalSizes,
    setSelectedAssembly,
    shouldCallShopProducts
  } = props;
  const [optionalSizes, setOptionalSizes] = useState<OptionalSizes[] | null>(
    null
  );
  const [isLoadingOptionalSizes, setIsLoadingOptionalSizes] = useState(false);
  const [isOriginalSizeOpen, setIsOriginalSizeOpen] = useState(false);
  const [isOptionalSizesOpen, setIsOptionalSizesOpen] = useState(false);
  const [selectedOptionalSize, setSelectedOptionalSize] = useState(null);

  const [selectedOptionalSizeGrouping, setSelectedOptionalSizeGrouping] =
    useState<OptionalSizes | null>(null);
  const { staggeredAssemblies, standardAssemblies } = Array.isArray(assemblies)
    ? assemblies.reduce(
        (grouping: Grouping, option) => {
          const parsedAssembly = parseAssemblyType(option) as Assembly;
          if (parsedAssembly?.frontTireDescription) {
            grouping.staggeredAssemblies.push(parsedAssembly);
          } else {
            grouping.standardAssemblies.push(parsedAssembly);
          }
          return grouping;
        },
        { staggeredAssemblies: [], standardAssemblies: [] }
      )
    : { staggeredAssemblies: null, standardAssemblies: null };

  const hasStockRideHeight = /stock height/i.test(
    selectedRideHeight?.description || ''
  );

  const onAssemblySelected = useCallback(
    selectedAssembly => {
      getOptionalSizes(
        getParam(
          selectedAssembly,
          isYearAfterModel ? selectedYear : selectedModel,
          selectedTrim,
          selectedRideHeight
        )
      ).then(optionalSizeData => {
        const optionalSizes: OptionalSizes[] = [];
        let selectedFrontTireSize;
        let selectedRearTireSize;
        let selectedTireSize;

        if (selectedAssembly.frontTireSize) {
          selectedFrontTireSize = parseTireSize(selectedAssembly.frontTireSize);
          selectedRearTireSize = parseTireSize(selectedAssembly.rearTireSize);
        } else {
          selectedTireSize = parseTireSize(selectedAssembly.description);
        }

        function addTireSize(
          wheelSize,
          tireSize,
          offset,
          isStaggered,
          matchQualifier,
          diameter
        ) {
          let matchingGrouping = optionalSizes.find(
            group => group.offset === offset
          );
          if (!matchingGrouping) {
            matchingGrouping = {
              offset,
              oversizedSizes: [],
              oversizedSizesStaggered: [],
              sizes: [],
              sizesStaggered: [],
              undersizedSizes: [],
              undersizedSizesStaggered: [],
              wheelSize
            };
            optionalSizes.push(matchingGrouping);
          }
          if (matchingGrouping.wheelSize === diameter) {
            let sizeArrayKey;
            if (matchQualifier === 'undersize') {
              sizeArrayKey = 'undersizedSizes';
            } else if (matchQualifier === 'match') {
              sizeArrayKey = 'sizes';
            } else {
              sizeArrayKey = 'oversizedSizes';
            }

            if (isStaggered) {
              sizeArrayKey += 'Staggered';
            }

            let sizeArray = matchingGrouping[sizeArrayKey];

            if (!sizeArray.find(size => size.id === tireSize.id)) {
              sizeArray.push(tireSize);
            }
          }
        }

        optionalSizeData.tireSizes?.forEach(item => {
          const wheelSize = parseFloat(item.wheelSize);
          const offset = item.sizeQualifier;

          if (
            optionalSizes.find(group => group.offset === offset) ||
            item.axleType !== AXLE_TYPES.BOTH
          ) {
            return;
          }

          item.optionalTireSizes.forEach(optionalTireSize => {
            const tireSize = {
              description: optionalTireSize.tireSize,
              id: `optional-size-${optionalTireSize.tireSize}`
            };

            const size = parseTireSize(tireSize.description);

            if (
              selectedTireSize?.diameter !== size?.diameter ||
              selectedTireSize?.aspectRatio !== size?.aspectRatio ||
              selectedTireSize?.width !== size?.width
            ) {
              addTireSize(
                String(wheelSize),
                tireSize,
                offset,
                false,
                optionalTireSize.matchQualifier,
                size?.diameter
              );
            }
          });
        });

        optionalSizeData.optionalStaggeredSet?.forEach(item => {
          const tireSize = {
            frontTireDescription: item.size.front,
            id: `optional-size-${item.size.front}-${item.size.rear}`,
            rearTireDescription: item.size.rear
          };
          const frontTireSize = parseTireSize(tireSize.frontTireDescription);
          const rearTireSize = parseTireSize(tireSize.rearTireDescription);

          const wheelSize = frontTireSize?.diameter
            ? parseFloat(frontTireSize?.diameter)
            : '';
          const offset = item.offset;

          if (
            selectedFrontTireSize?.diameter !== frontTireSize?.diameter ||
            selectedFrontTireSize?.aspectRatio !== frontTireSize?.aspectRatio ||
            selectedFrontTireSize?.width !== frontTireSize?.width ||
            selectedRearTireSize?.diameter !== rearTireSize?.diameter ||
            selectedRearTireSize?.aspectRatio !== rearTireSize?.aspectRatio ||
            selectedRearTireSize?.width !== rearTireSize?.width
          ) {
            addTireSize(
              String(wheelSize),
              tireSize,
              String(offset),
              true,
              item.matchQualifier,
              frontTireSize?.diameter
            );
          }
        });

        optionalSizes.sort(byOffset);
        setOptionalSizes(optionalSizes);
        setIsLoadingOptionalSizes(false);

        if (selectedFitment?.isOE || selectedAssembly.isOE || !isEditing) {
          onEvent({
            name: EVENTS.VEHICLE_ASSEMBLY_SELECTED,
            payload: { assembly: selectedAssembly }
          });
          return;
        }

        const matchingOptionalSizeGroup = optionalSizes.find(
          group => group.offset === selectedFitment?.sizeQualifier
        );

        if (matchingOptionalSizeGroup) {
          let matchingOptionalSize;

          if (selectedFitment?.rearTireSize) {
            for (const sizes of [
              matchingOptionalSizeGroup.sizesStaggered,
              matchingOptionalSizeGroup.undersizedSizesStaggered,
              matchingOptionalSizeGroup.oversizedSizesStaggered
            ]) {
              const match = sizes.find(
                size =>
                  toFitmentSize(size.frontTireDescription) ===
                    selectedFitment?.frontTireSize &&
                  toFitmentSize(size.rearTireDescription) ===
                    selectedFitment?.rearTireSize
              );
              if (match) {
                matchingOptionalSize = match;
                break;
              }
            }
          } else {
            for (const sizes of [
              matchingOptionalSizeGroup.sizes,
              matchingOptionalSizeGroup.undersizedSizes,
              matchingOptionalSizeGroup.oversizedSizes
            ]) {
              const match = sizes.find(
                size =>
                  toFitmentSize(size.description) ===
                  selectedFitment?.frontTireSize
              );
              if (match) {
                matchingOptionalSize = match;
                break;
              }
            }
          }

          if (matchingOptionalSize) {
            setSelectedOptionalSizeGrouping(matchingOptionalSizeGroup);
            setSelectedOptionalSize(matchingOptionalSize);
            setIsOptionalSizesOpen(true);
            setIsViewingOptionalSizes(true);
            onEvent({
              name: EVENTS.VEHICLE_ASSEMBLY_OPTIONAL_TIRE_SIZE_SELECTED,
              payload: { assembly: matchingOptionalSize }
            });
            return;
          }
        }

        onEvent({
          name: EVENTS.VEHICLE_ASSEMBLY_SELECTED,
          payload: { assembly: selectedAssembly }
        });
      });
    },
    [
      getOptionalSizes,
      isYearAfterModel,
      selectedYear,
      selectedModel,
      selectedTrim,
      selectedRideHeight,
      selectedFitment?.isOE,
      selectedFitment?.sizeQualifier,
      selectedFitment?.rearTireSize,
      selectedFitment?.frontTireSize,
      isEditing,
      onEvent,
      setIsViewingOptionalSizes
    ]
  );

  const selectAssembly = useCallback(
    assembly => {
      setIsLoadingOptionalSizes(true);
      setSelectedAssembly(assembly);
      setSelectedOptionalSize(null);
      setSelectedOptionalSizeGrouping(null);
      setIsOriginalSizeOpen(true);
      onEvent({
        name: EVENTS.VEHICLE_ASSEMBLY_SELECTED,
        payload: { assembly, skipStateCheck: true }
      });
    },
    [setSelectedAssembly, onEvent]
  );

  const selectOptionalSize = useCallback(
    optionalSize => {
      setSelectedOptionalSize(optionalSize);
      onEvent({
        name: EVENTS.VEHICLE_ASSEMBLY_OPTIONAL_TIRE_SIZE_SELECTED,
        payload: { assembly: optionalSize, skipStateCheck: true }
      });
    },
    [setSelectedOptionalSize, onEvent]
  );

  const shopProducts = useCallback(
    () =>
      onShopProducts(
        selectedAssembly,
        selectedOptionalSize,
        selectedOptionalSizeGrouping?.offset
      ),
    [
      onShopProducts,
      selectedAssembly,
      selectedOptionalSize,
      selectedOptionalSizeGrouping
    ]
  );

  useEffect(() => {
    if (selectedAssembly && selectedTrim && selectedModel) {
      onAssemblySelected(selectedAssembly);
    } else {
      setSelectedOptionalSize(null);
    }
  }, [onAssemblySelected, selectedAssembly, selectedModel, selectedTrim]);

  useEffect(() => {
    if (shouldCallShopProducts) {
      shopProducts();
      onEvent({
        name: EVENTS.SHOP_PRODUCTS_CALLED
      });
    }
  }, [onEvent, shopProducts, shouldCallShopProducts]);

  return {
    hasStockRideHeight,
    isLoadingOptionalSizes,
    isOptionalSizesOpen,
    isOriginalSizeOpen,
    optionalSizes,
    selectAssembly,
    selectOptionalSize,
    selectedOptionalSize,
    selectedOptionalSizeGrouping,
    setIsOptionalSizesOpen,
    setSelectedOptionalSize,
    setSelectedOptionalSizeGrouping,
    shopProducts,
    staggeredAssemblies,
    standardAssemblies
  };
}
